/**
 * Makes `removeChild`/`insertBefore` resilient to browser/Google Translate mutating the DOM
 * out from under React.
 *
 * Translate rewrites text nodes by wrapping them in nested `<font>` elements without React's
 * knowledge. When React later tries to remove/insert a node in that mutated subtree (e.g.
 * during a client-side route change), the live DOM no longer matches what React's Fiber tree
 * expects, and it throws - a `NotFoundError` on `removeChild`/`insertBefore`, or (in the
 * `RangeError: Maximum call stack size exceeded` cases we've seen on `/login` and `/sign-up`
 * in Bugsnag/LogRocket - see PLA-1259) a stack overflow while walking the corrupted tree.
 *
 * This is a known, not-fixable-inside-React class of bug. The React team's own conclusion,
 * and the workaround this function applies verbatim, come from Dan Abramov (React core team):
 * @see https://github.com/facebook/react/issues/11538#issuecomment-417504600
 * @see https://bugs.chromium.org/p/chromium/issues/detail?id=872770 (Chromium bug the React
 *   team filed asking Google to fix this at the browser level; no traction as of this writing)
 *
 * Trade-off, from that same comment: "It will make your app a little bit slower" - this
 * patches `removeChild`/`insertBefore` globally, so every DOM removal/insertion across the
 * whole app (not just on pages being translated) now goes through an extra `parentNode`
 * identity check instead of calling straight through to the native method.
 *
 * This is a well-documented fix for the `NotFoundError` variant specifically. Its effect on
 * the `RangeError: Maximum call stack size exceeded` variant we're actually chasing is
 * unverified - we don't have a reliable local repro, since it depends on the real browser
 * translator's mutation timing.
 */
export const installGoogleTranslateResiliencePatch = (nodeConstructor: typeof Node | undefined = globalThis.Node) => {
  if (typeof nodeConstructor !== 'function' || !nodeConstructor.prototype) {
    return;
  }

  const { prototype } = nodeConstructor;

  const originalRemoveChild = prototype.removeChild;
  prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.error('Cannot remove a child from a different parent', child, this);
      return child;
    }

    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = prototype.insertBefore;
  prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.error('Cannot insert before a reference node from a different parent', referenceNode, this);
      return newNode;
    }

    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};
